import { useTranslation } from '@hbs-us/ui';
import lockupUrl from '@hbs-us/ui/assets/qelm-lockup.svg';
import { type FormEvent, useId, useMemo, useState } from 'react';

import { setLanguage } from '../i18n.js';
import { ApiError } from '../lib/apiFetch.js';
import { currentWorkspaceHost } from '../lib/workspaceHost.js';
import { login } from './authClient.js';
import { TurnstileWidget } from './TurnstileWidget.js';

/**
 * Tenant sign-in screen (rendered by AuthGuard when unauthenticated).
 *
 * A branded SINGLE-STEP flow against `POST /api/v1/auth/login` (email +
 * password). Tenant users have no MFA this phase, so there is no TOTP step.
 * On success we call `onSignedIn` so the guard resolves the session from
 * `/api/v1/me`. The login response is NOT a substitute for that: it carries only
 * `user` and `tenant`, never `features` / `analytics` / `impersonation`, so a
 * shell built from it renders the wrong nav until the page is reloaded. See the
 * measured payloads in AuthGuard's header. The access + refresh tokens are stored
 * by the `login` client (access in memory, threat T-20-09); this screen never
 * touches storage.
 *
 * ── WHY THIS LOOKS THE WAY IT DOES ──────────────────────────────────────────
 * Signing in crosses two runtimes. A person clicks "Log in" on the marketing
 * site, lands on the server-rendered finder at `go.<zone>`, types a subdomain,
 * and gets 302'd HERE, into a client-rendered React SPA on a different host.
 * They should never be able to tell. So this screen renders the SAME markup
 * and the SAME class names as `workers/tenant-router/src/find-workspace.tsx`,
 * both driven by `@hbs-us/ui/auth-shell.css` — logo above the card, heading
 * outside it, one 420px column, the accent wash behind. Read that stylesheet
 * before changing anything visual here; a change on one surface that skips the
 * other reintroduces the seam.
 *
 * The screen also pins itself to `.dark`. Signed-OUT is always the dark front
 * door, matching the finder; a signed-IN user's own appearance preference is
 * untouched and takes over the moment the session resolves.
 *
 * Error ROUTING — the api host returns a stable `error` code surfaced as
 * `ApiError.message`. We map each known code to a friendly i18n string and fall
 * back to a generic message for anything else. A raw code is NEVER shown:
 *   invalid_login                → "That email or password didn't match."
 *   too_many_attempts            → "Too many tries. Is the address above your company's?…"
 *   anything else / network err  → generic "Couldn't sign in…"
 */

/** Maps a thrown error to the i18n key for the inline message (never a raw code). */
function errorKeyFor(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.message) {
      case 'invalid_login':
      case 'email_and_password_required':
        return 'invalidLogin';
      case 'too_many_attempts':
        return 'rateLimited';
      case 'turnstile_required':
        // Not a credential failure. The hint below the field explains the step; this line only
        // covers a REPEAT refusal, where saying nothing would look like the button did nothing.
        return 'challengeHint';
      default:
        return 'genericError';
    }
  }
  return 'genericError';
}

export function LoginScreen({ onSignedIn }: { onSignedIn: () => void | Promise<void> }) {
  const { t, i18n } = useTranslation();
  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // A password box with no placeholder and no reveal reads as a dead field next to an
  // email box that shows `you@example.com`. The toggle is a WORD, not a lone eye glyph:
  // the two eye icons for on and off are told apart by a diagonal slash a few pixels
  // wide, and nobody agrees which one means "showing".
  const [passwordVisible, setPasswordVisible] = useState(false);
  // Empty until the server asks for a challenge; it also supplies the key to render it with.
  const [challengeSiteKey, setChallengeSiteKey] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  // Bumped after every failed attempt so the widget remounts and mints a fresh single-use token.
  const [challengeNonce, setChallengeNonce] = useState(0);
  const [challengeUnavailable, setChallengeUnavailable] = useState(false);

  // Which workspace they landed on. Derived from location — no request, so it
  // renders on the very first paint alongside everything else.
  const { host, workspace, finderUrl } = useMemo(() => currentWorkspaceHost(), []);

  // Password reset lives on THIS host. `/reset` is one of the tenant host's
  // pinned auth prefixes (AUTH_LANDING_PATH_PREFIXES,
  // workers/tenant-router/src/worker.ts), so the tenant router hands it to the
  // api worker's server-rendered request form instead of the SPA shell.
  //
  // It used to point at `${finderUrl}/reset` on go.<env>, which worked but sent
  // the person to a different host and left them there: that page's way back is
  // `/login`, and `go.<env>/login` is not a route — it renders the workspace
  // finder, so they had to name their workspace a second time. A root-relative
  // href keeps the whole loop on the workspace they are already signing in to,
  // and stays correct on `{tenant}.localhost:5173` where an absolute https URL
  // would not. Gated on `workspace` for the same reason the finder link is:
  // there is no tenant host to reset a password on.
  const resetUrl = workspace ? '/reset' : null;

  const canSubmit = email.trim() !== '' && password !== '' && !submitting;
  const activeLang = i18n.language?.startsWith('es') ? 'es' : 'en';

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    setErrorKey(null);
    setSubmitting(true);
    try {
      // `|| undefined` so an unchallenged sign-in sends no token field at all.
      await login(email.trim().toLowerCase(), password, turnstileToken || undefined);
    } catch (err) {
      // The server decides when a challenge is needed and sends the public site key with the
      // refusal, so this screen never carries its own copy of the key.
      if (err instanceof ApiError && err.message === 'turnstile_required') {
        const siteKey = String(err.details?.turnstileSiteKey ?? '');
        if (siteKey) setChallengeSiteKey(siteKey);
      }
      // A Turnstile token is single use. Whatever went wrong, the one we just sent is spent, so
      // drop it and remount the widget for a fresh one rather than resubmitting a dead token.
      setTurnstileToken('');
      setChallengeNonce((n) => n + 1);
      setErrorKey(errorKeyFor(err));
      setSubmitting(false);
      return;
    }

    // Signed in. Past this line a failure is the app failing to LOAD, not a credential
    // being wrong, and saying "that email or password didn't match" would send someone
    // round the same loop retyping a password that already worked. The screen stays
    // mounted, so the email they typed is still there for the retry.
    try {
      await onSignedIn();
    } catch {
      setErrorKey('sessionUnavailable');
      setSubmitting(false);
    }
  };

  return (
    <main className="dark fw-page">
      <div className="fw-shell">
        <img className="fw-logo" src={lockupUrl} alt="QELM" width={170} height={34} />

        <header className="fw-head">
          <h1>{t('common:login.heading')}</h1>
          <p className="fw-sub">{t('common:login.subheading')}</p>
          {workspace ? (
            <p className="fw-workspace">
              {t('common:login.workspaceLead')} <b>{host}</b>
            </p>
          ) : null}
        </header>

        <section className="fw-card" aria-labelledby="fw-title">
          <h2 id="fw-title" className="fw-sr">
            {t('common:login.heading')}
          </h2>

          {/* Reserved slot: an arriving error must not shove the button under a
              moving finger mid-tap. */}
          <div className="fw-error-slot">
            {errorKey ? (
              <p id={errorId} className="fw-error" role="alert" aria-live="assertive">
                {t(`common:login.${errorKey}`)}
              </p>
            ) : null}
          </div>

          {/*
            No `noValidate`: both fields are `required`, so the browser blocks an
            empty submit exactly as it does on the finder's form. The button is
            never disabled on empty input — a greyed-out button that will not say
            why is worse than the native prompt, and the finder does not do it.
          */}
          <form onSubmit={submit}>
            <div className="fw-field">
              <label className="fw-label" htmlFor={emailId}>
                {t('common:login.emailLabel')}
              </label>
              <div className="fw-input-row">
                <input
                  className="fw-input"
                  id={emailId}
                  type="email"
                  name="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('common:login.emailPlaceholder')}
                  aria-invalid={errorKey ? true : undefined}
                  aria-describedby={errorKey ? errorId : undefined}
                />
              </div>
            </div>

            <div className="fw-field">
              {/*
                The reset link rides the password label's own row, right-aligned
                against the input's edge. It is a plain link, not a control: it
                leaves the SPA for a server-rendered page on this same host, and
                it must not compete with the submit button for attention.
              */}
              <div className="fw-label-row">
                <label className="fw-label" htmlFor={passwordId}>
                  {t('common:login.passwordLabel')}
                </label>
                {resetUrl ? (
                  <a className="fw-field-link" href={resetUrl}>
                    {t('common:login.forgotPassword')}
                  </a>
                ) : null}
              </div>
              <div className="fw-input-row">
                <input
                  className="fw-input"
                  id={passwordId}
                  type={passwordVisible ? 'text' : 'password'}
                  name="password"
                  placeholder={t('common:login.passwordPlaceholder')}
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={errorKey ? true : undefined}
                  aria-describedby={errorKey ? errorId : undefined}
                />
                {/*
                  `fw-suffix` is the shared style for a control riding inside the input's
                  border — the finder uses it for the `.qelm.app` tail. Reusing it keeps
                  this button on the auth palette without touching auth-shell.css, which is
                  byte-pinned against the tenant-router's inlined copy.
                */}
                {/*
                  The label changes, so it is already the state readout: "Show" means the
                  password is hidden, "Hide" means it is visible. Adding aria-pressed on top
                  gives a screen reader two state signals for one control that contradict
                  each other on read-out ("Hide, pressed").
                */}
                <button
                  type="button"
                  className="fw-suffix"
                  aria-controls={passwordId}
                  onClick={() => setPasswordVisible((visible) => !visible)}
                >
                  {t(passwordVisible ? 'common:login.hidePassword' : 'common:login.showPassword')}
                </button>
              </div>
            </div>

            {/*
              Only ever present when the SERVER asked for it, which is after several failed
              attempts. It sits above the button, in a normal field slot, so an arriving widget
              pushes the button down instead of appearing under a finger already reaching for it.

              The button is NOT disabled while the challenge is unsolved. This form's rule is that
              a greyed-out button which will not say why is worse than a clear instruction, and
              the hint above says exactly what to do.
            */}
            {challengeSiteKey ? (
              <div className="fw-field">
                <p className="fw-hint">
                  {t(
                    challengeUnavailable
                      ? 'common:login.challengeUnavailable'
                      : 'common:login.challengeHint',
                  )}
                </p>
                {challengeUnavailable ? null : (
                  <TurnstileWidget
                    key={challengeNonce}
                    siteKey={challengeSiteKey}
                    onToken={setTurnstileToken}
                    onUnavailable={() => setChallengeUnavailable(true)}
                  />
                )}
              </div>
            ) : null}

            {/*
              Disabled only while a request is in flight, to stop a double submit.
              `data-busy` drives the same progress sweep the finder's button runs,
              from the same stylesheet, so the two steps of one sign-in behave
              identically. It is set only while the server is genuinely working.
            */}
            <button
              className="fw-submit"
              type="submit"
              disabled={submitting}
              data-busy={submitting ? 'true' : undefined}
            >
              {submitting ? t('common:login.submitting') : t('common:login.submit')}
            </button>
          </form>

          <div className="fw-secondary">
            <p>
              <strong>{t('common:login.helpStrong')}</strong>
              {t('common:login.helpRest')}
            </p>
            {finderUrl ? (
              <p>
                {t('common:login.wrongWorkspace')}{' '}
                <a href={finderUrl}>{t('common:login.switchWorkspace')}</a>
              </p>
            ) : null}
          </div>
        </section>

        <p className="fw-lang">
          {activeLang === 'en' ? (
            <span className="is-active">EN</span>
          ) : (
            <button type="button" onClick={() => setLanguage('en')} lang="en">
              EN
            </button>
          )}
          {' · '}
          {activeLang === 'es' ? (
            <span className="is-active">ES</span>
          ) : (
            <button type="button" onClick={() => setLanguage('es')} lang="es">
              ES
            </button>
          )}
        </p>
      </div>
    </main>
  );
}
